/**
 * USER ENTITY
 * ===========
 * Represents a user in the The Idioma Club system.
 */

export type UserRole = 'super_admin' | 'student' | 'teacher' | 'admin' | 'guest';
export type UserStatus = 'active' | 'suspended' | 'pending' | 'inactive' | 'banned';

import { UserId, CohortId, TenantId } from '../BrandedTypes';

export interface UserData {
    id: UserId;
    username: string;
    email: string;
    full_name?: string;
    name?: string; // Legacy/Store compatibility
    avatar_url?: string;
    avatar?: string; // Legacy/Store compatibility
    role: UserRole;
    status: UserStatus;
    phone?: string;
    created_at?: string;
    valid_until?: string;
    health_score?: number;

    total_xp?: number;
    streak_count?: number;
    lives?: number;
    gems?: number;
    last_seen_at?: string;
    cohort_id?: CohortId;
    cohort_name?: string;
    tenant_id?: TenantId;
    current_node_id?: string;
    tenants?: {
        name: string;
        code: string;
        config?: Record<string, unknown>;
    };
}

export class User {
    public readonly id: UserId;
    public readonly username: string;
    public readonly email: string;
    public readonly fullName: string;
    public readonly role: UserRole;
    public readonly status: UserStatus;
    public readonly avatarUrl: string;
    public readonly totalXp: number;
    public readonly streak: number;
    public readonly lives: number;
    public readonly gems: number;
    public readonly phone: string;
    public readonly createdAt: Date | null;
    public readonly validUntil: Date | null;
    public readonly healthScore: number;

    public readonly lastSeenAt: string | null;
    public readonly current_node_id: string | null;
    public readonly tenantId: TenantId | null;
    public readonly cohortId: CohortId | null;
    public readonly cohortName: string | null;

    constructor(data: UserData) {
        this.id = data.id;
        this.username = data.username || '';
        this.email = data.email || '';
        this.fullName = data.full_name || data.name || 'Estudiante';
        this.role = data.role || 'guest';
        this.status = data.status || 'pending';
        this.avatarUrl = data.avatar_url || data.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${this.username || this.id}`;
        this.totalXp = data.total_xp || 0;
        this.streak = data.streak_count ?? 0;
        this.lives = data.lives ?? 5;
        this.gems = data.gems ?? 0;

        this.phone = data.phone || '';
        this.createdAt = data.created_at ? new Date(data.created_at) : null;
        this.validUntil = data.valid_until ? new Date(data.valid_until) : null;
        this.healthScore = data.health_score || 0;

        this.lastSeenAt = data.last_seen_at || null;
        this.current_node_id = data.current_node_id || null;
        this.tenantId = data.tenant_id || null;
        this.cohortId = data.cohort_id || null;
        this.cohortName = data.cohort_name || null;
    }

    get isAdmin(): boolean {
        return ['super_admin', 'admin'].includes(this.role);
    }

    get isTeacher(): boolean {
        return this.role === 'teacher';
    }

    get isStudent(): boolean {
        return this.role === 'student';
    }

    get isActive(): boolean {
        return this.status === 'active';
    }

    get initials(): string {
        return this.fullName
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
    }

    with(changes: Partial<UserData>): User {
        return new User({
            ...this,
            id: this.id,
            username: this.username,
            email: this.email,
            full_name: this.fullName,
            role: this.role,
            status: this.status,
            avatar_url: this.avatarUrl,
            phone: this.phone,
            created_at: this.createdAt?.toISOString(),
            valid_until: this.validUntil?.toISOString(),
            health_score: this.healthScore,

            total_xp: this.totalXp,
            streak_count: this.streak,
            lives: this.lives,
            gems: this.gems,
            tenant_id: this.tenantId || undefined,
            current_node_id: this.current_node_id || undefined,
            last_seen_at: this.lastSeenAt || undefined,
            cohort_id: this.cohortId || undefined,
            cohort_name: this.cohortName || undefined,
            ...changes
        } as UserData);
    }

    withStatus(newStatus: UserStatus): User {
        return this.with({ status: newStatus });
    }
}

export interface UserWithCohortsData extends UserData {
    cohorts?: {
        id: CohortId;
        name: string;
        role: string;
    }[];
}

export class UserWithCohorts extends User {
    public readonly cohorts: { id: CohortId; name: string; role: string }[];

    constructor(data: UserWithCohortsData) {
        super(data);
        this.cohorts = (data.cohorts || []) as { id: CohortId; name: string; role: string }[];
    }

    withCohorts(newCohorts: { id: CohortId; name: string; role: string }[]): UserWithCohorts {
        return new UserWithCohorts({
            ...this,
            id: this.id,
            username: this.username,
            email: this.email,
            full_name: this.fullName,
            role: this.role,
            status: this.status,
            avatar_url: this.avatarUrl,
            cohorts: newCohorts,
            phone: this.phone,
            created_at: this.createdAt?.toISOString(),
            valid_until: this.validUntil?.toISOString(),
            health_score: this.healthScore,

            total_xp: this.totalXp,
            streak_count: this.streak,
            lives: this.lives,
            gems: this.gems,
            tenant_id: this.tenantId || undefined,
            current_node_id: this.current_node_id || undefined,
            last_seen_at: this.lastSeenAt || undefined
        } as UserWithCohortsData);
    }

    override withStatus(newStatus: UserStatus): UserWithCohorts {
        return new UserWithCohorts({
            ...this,
            id: this.id,
            username: this.username,
            email: this.email,
            full_name: this.fullName,
            role: this.role,
            status: newStatus,
            avatar_url: this.avatarUrl,
            cohorts: this.cohorts,
            phone: this.phone,
            created_at: this.createdAt?.toISOString(),
            valid_until: this.validUntil?.toISOString(),
            health_score: this.healthScore,

            total_xp: this.totalXp,
            streak_count: this.streak,
            lives: this.lives,
            gems: this.gems,
            tenant_id: this.tenantId || undefined,
            current_node_id: this.current_node_id || undefined,
            last_seen_at: this.lastSeenAt || undefined
        } as UserWithCohortsData);
    }
}
